
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Lock, ShieldCheck, Key, ArrowRight, ShieldAlert, Fingerprint, Loader2, AlertCircle } from 'lucide-react';
import { GlowingEffect } from '../ui/GlowingEffect';

interface AccessGateProps {
  onBack: () => void;
  onGrantAccess?: () => void;
  isAdmin?: boolean;
  branding?: {
    creatorName: string;
    logoUrl: string;
    accentColor: string;
  };
}

export function AccessGate({ onBack, onGrantAccess, branding, isAdmin = false }: AccessGateProps) {
  const [email, setEmail] = useState('');
  const [key, setKey] = useState('');
  const [status, setStatus] = useState<'idle' | 'validating' | 'success' | 'denied'>('idle');
  const [bypassActive, setBypassActive] = useState(false);

  const handleAccess = (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'validating') return;
    
    setStatus('validating');
    // Simulated smooth network delay for "Authorized Feeling"
    setTimeout(() => {
      // In a real app, you would check if the token/key exists in Supabase
      // For this implementation, we accept 'VALID' or any non-empty string for the demo token logic
      if (key.trim().length > 0) {
        setStatus('success');
        if (onGrantAccess) setTimeout(onGrantAccess, 1800);
      } else {
        setStatus('denied');
        setTimeout(() => setStatus('idle'), 3000);
      }
    }, 2000);
  };

  const handleMasterBypass = () => {
    setBypassActive(true);
    setStatus('validating');
    setTimeout(() => {
      setStatus('success');
      if (onGrantAccess) setTimeout(onGrantAccess, 1200);
    }, 1200);
  };

  const accentColor = branding?.accentColor || 'hsl(var(--primary))';

  return (
    <div className="min-h-screen bg-black text-foreground flex flex-col items-center justify-center px-6 selection:bg-primary selection:text-primary-foreground relative overflow-hidden">
      {/* Signature Landing Page Style Background Ambience */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none opacity-40">
        <div className="absolute top-[-20%] left-[-20%] size-[1000px] bg-primary/5 blur-[180px] rounded-full" />
        <div className="absolute bottom-[-20%] right-[-20%] size-[1000px] bg-primary/5 blur-[180px] rounded-full" />
      </div>

      <div className="fixed top-8 left-8 flex items-center gap-4 z-50">
        <div className="relative rounded-full transform-gpu">
           <GlowingEffect spread={20} glow={true} disabled={false} proximity={40} inactiveZone={0.01} borderWidth={1} />
           <button onClick={onBack} className="relative flex items-center gap-2 text-[10px] font-bold tracking-[0.3em] uppercase text-muted-foreground hover:text-foreground transition-all bg-background/50 px-5 py-2.5 rounded-full border border-border/40 hover:scale-95 backdrop-blur-md">
            <ArrowLeft size={12} /> Back
          </button>
        </div>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md space-y-12 relative z-10"
      >
        <AnimatePresence mode="wait">
          {status === 'success' ? (
            <motion.div 
              key="success"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="text-center space-y-12"
            >
              <div className="relative size-32 mx-auto">
                <motion.div 
                  initial={{ scale: 0.8, opacity: 0 }} 
                  animate={{ scale: 1.4, opacity: 0.2 }} 
                  transition={{ duration: 2, repeat: Infinity, repeatType: 'reverse' }}
                  className="absolute inset-0 rounded-full blur-3xl"
                  style={{ backgroundColor: bypassActive ? '#FFFFFF' : accentColor }}
                />
                <div className="relative size-full rounded-full flex items-center justify-center shadow-[0_0_50px_rgba(255,255,255,0.1)] transition-colors duration-1000 bg-white border border-white/20">
                  {bypassActive ? (
                    <ShieldAlert size={48} className="text-black" />
                  ) : (
                    <ShieldCheck size={48} className="text-black" />
                  )}
                </div>
              </div>
              <div className="space-y-4">
                <h1 className="text-5xl font-light tracking-tighter">{bypassActive ? 'Protocol Bypassed' : 'Authorization Verified'}</h1>
                <p className="text-muted-foreground font-light text-base leading-relaxed max-w-xs mx-auto opacity-60">
                  {bypassActive ? 'Master keys accepted. Decrypting secure cluster.' : 'Your unique access session has been verified and encrypted.'}
                </p>
              </div>
              <div className="flex flex-col items-center gap-6">
                 <div className="flex items-center gap-4">
                   <Loader2 className="size-4 animate-spin text-primary opacity-40" />
                   <span className="text-[10px] font-bold tracking-[0.5em] uppercase text-foreground">Synchronizing Portal Data...</span>
                 </div>
                 <div className="h-[2px] w-48 bg-white/5 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }} 
                      animate={{ width: '100%' }} 
                      transition={{ duration: 1.8, ease: "circInOut" }} 
                      className="h-full bg-white shadow-[0_0_15px_rgba(255,255,255,0.8)]" 
                    />
                 </div>
              </div>
            </motion.div>
          ) : (
            <motion.div key="form" exit={{ opacity: 0, scale: 0.98, filter: 'blur(10px)' }} transition={{ duration: 0.6 }} className="space-y-12">
              <div className="text-center space-y-10">
                <div className="size-28 bg-muted/10 border border-border/40 rounded-[40px] mx-auto flex items-center justify-center relative overflow-hidden group transition-all duration-700 hover:scale-105 hover:border-primary/40 shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50" />
                  <GlowingEffect spread={80} glow={true} disabled={false} proximity={0} inactiveZone={0} borderWidth={1.5} />
                  {branding?.logoUrl ? (
                    <img src={branding.logoUrl} alt={branding.creatorName} className="size-16 object-contain relative z-10 transition-transform duration-500 group-hover:scale-110" />
                  ) : (
                    <span className="text-5xl font-light relative z-10">{branding?.creatorName?.[0] || 'A'}</span>
                  )}
                </div>
                <div className="space-y-4">
                  <h1 className="text-5xl font-light tracking-tighter">{branding?.creatorName || 'Aure Secure OS'}</h1>
                  <div className="flex items-center justify-center gap-4 opacity-40">
                    <div className="h-px w-10 bg-white/40" />
                    <p className="text-[10px] tracking-[0.6em] uppercase font-bold">Encrypted Node</p>
                    <div className="h-px w-10 bg-white/40" />
                  </div>
                </div>
              </div>

              <form onSubmit={handleAccess} className="space-y-8 bg-card/20 backdrop-blur-3xl p-10 rounded-[48px] border border-white/5 relative shadow-2xl">
                <GlowingEffect spread={100} glow={true} disabled={false} proximity={0} inactiveZone={0} borderWidth={1} />
                <div className="space-y-6 relative z-10">
                  <div className="space-y-2">
                    <label className="text-[9px] font-bold tracking-[0.5em] uppercase text-muted-foreground/40 ml-2">Verified Identity</label>
                    <div className="relative group">
                       <input 
                        type="email" 
                        required
                        placeholder="your-whop-email@example.com" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-background/50 border border-border/20 rounded-2xl py-5 px-6 outline-none focus:border-white/20 transition-all text-sm font-light placeholder:text-muted-foreground/10 text-foreground" 
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] font-bold tracking-[0.5em] uppercase text-muted-foreground/40 ml-2">Access Key</label>
                    <div className="relative group">
                      <input 
                        type="password" 
                        required
                        placeholder="•••• •••• •••• ••••" 
                        value={key}
                        onChange={(e) => setKey(e.target.value)}
                        className="w-full bg-background/50 border border-border/20 rounded-2xl py-5 px-6 outline-none focus:border-white/20 transition-all text-sm font-light placeholder:text-muted-foreground/20 tracking-[0.4em] text-foreground uppercase" 
                      />
                      <Key className="absolute right-6 top-1/2 -translate-y-1/2 text-muted-foreground/10 group-focus-within:text-white/40 transition-colors" size={18} />
                    </div>
                  </div>
                </div>

                {status === 'denied' && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-5 rounded-2xl bg-destructive/10 border border-destructive/20 text-center">
                    <p className="text-[10px] font-bold tracking-widest uppercase text-destructive flex items-center justify-center gap-3">
                       <AlertCircle size={14} /> Key Revoked or Invalid
                    </p>
                  </motion.div>
                )}

                <div className="space-y-4 relative z-10">
                  <button 
                    type="submit" 
                    disabled={status === 'validating'} 
                    className="group relative w-full py-5 rounded-2xl bg-white text-black text-[11px] font-bold tracking-[0.6em] uppercase transition-all hover:scale-[0.98] shadow-[0_0_30px_rgba(255,255,255,0.1)] active:scale-95 disabled:opacity-50 overflow-hidden"
                  >
                    <GlowingEffect spread={80} glow={true} disabled={false} proximity={0} inactiveZone={0} borderWidth={2} variant="white" />
                    <span className="relative z-10 flex items-center justify-center gap-3">
                       {status === 'validating' ? (
                         <>
                           <Loader2 className="size-4 animate-spin" />
                           Verifying Security...
                         </>
                       ) : (
                         <>
                           Decrypt Access
                           <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                         </>
                       )}
                    </span>
                  </button>

                  {isAdmin && (
                    <motion.button
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      type="button"
                      onClick={handleMasterBypass}
                      disabled={status === 'validating'}
                      className="w-full group relative flex items-center justify-center gap-3 py-4 rounded-2xl border border-white/5 bg-white/[0.02] text-white/40 hover:text-white hover:bg-white/5 transition-all overflow-hidden"
                    >
                      <GlowingEffect spread={40} glow={true} disabled={false} proximity={100} inactiveZone={0.01} borderWidth={1.5} variant="white" />
                      <Fingerprint size={16} className="relative z-10 opacity-40 transition-all group-hover:scale-110 group-hover:opacity-100" />
                      <span className="text-[9px] font-bold tracking-[0.5em] uppercase relative z-10">Master Bypass Mode</span>
                    </motion.button>
                  )}
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <footer className="fixed bottom-12 flex flex-col items-center gap-6 opacity-30">
        <div className="flex items-center gap-4">
          <div className="h-px w-6 bg-white/20" />
          <div className="flex items-center gap-3">
            <Lock className="size-3 text-muted-foreground" />
            <span className="text-[9px] font-bold tracking-[0.5em] uppercase text-muted-foreground">End-to-End Encrypted Session</span>
          </div>
          <div className="h-px w-6 bg-white/20" />
        </div>
        <p className="text-[8px] tracking-[0.6em] text-muted-foreground/40 font-bold uppercase transition-all">AURE SECURITY ENGINE — V2.4.0</p>
      </footer>
    </div>
  );
}
